import {
  PlayerIdentityRecords,
  usePlayerIdentityRecords,
} from '../../identity/PlayerIdentityRecords.tsx';
import { AccountIdentityStatus } from '../account-identity-status.tsx';
import { OlderSubmissions } from '../kyc.tsx';
import type { KycRow, PlayerRecord } from '../types.ts';

/**
 * THE KYC TAB — where the account stands, how the player was verified, and what they sent through
 * the older process.
 *
 * First the identity status the account itself carries, which is what deposits, play and
 * withdrawals are checked against, with a sentence where a decided identity check says otherwise.
 * Then the identity record: the identity check is what verifies a player, and the record behind it
 * was readable only by a database query until this panel (staging, 15 Oct). Below it, the older
 * submissions, read-only and only where the player has any: that process closed on 23 October
 * 2026, and its approve, its refusal, its documents panel and the profile's "Review KYC" went with
 * it. The identity check is the only identity flow left, and so the only door a player it refuses
 * has.
 */
export function KycTab({
  player,
  submissions,
  canReview,
  role,
}: {
  player: PlayerRecord;
  /** The older submissions, newest first, from `GET /api/players/:id`. */
  submissions: KycRow[];
  /** Whether the reader holds kyc.review, which the identity record needs. */
  canReview: boolean;
  /** The reader's role, named when the identity record is withheld from it. */
  role: string;
}) {
  /* Asked for only by a role holding kyc.review; see readIdentityRecords. */
  const identity = usePlayerIdentityRecords(player.id, canReview);

  return (
    <>
      <AccountIdentityStatus status={player.kyc_status} read={identity} />
      <PlayerIdentityRecords read={identity} role={role} playerRef={player.ref} />
      {submissions.length > 0 && <OlderSubmissions submissions={submissions} />}
    </>
  );
}
